/**
 * Squares game constants used in the frontend (must match backend squares-constants when relevant).
 */

/** Coins charged per over when user enters a square (entry fee). */
export const SQUARES_ENTRANCE_FEE = 200;

/**
 * House rake by pool size (same curve as backend `rakeForEntrantCount`).
 * @param {number} n
 * @returns {number}
 */
export function rakeForEntrantCount(n) {
    const k = Math.floor(Number(n));
    if (!Number.isFinite(k) || k < 1) return 0.1;
    if (k === 4) return 0.03;
    if (k <= 6) return 0.05;
    return 0.1;
}

/**
 * First / second / third share of the post-rake pool (must sum to 1). Same as backend `prizeSharesForEntrantCount`.
 * @param {number} n
 * @returns {[number, number, number]}
 */
export function prizeSharesForEntrantCount(n) {
    const k = Math.floor(Number(n));
    if (k === 4) return [0.42, 0.31, 0.27];
    if (k === 5) return [0.46, 0.31, 0.23];
    return [0.5, 0.3, 0.2];
}

/**
 * Total post-rake prize pool in coins for a given pool entrant count (same formula as settlement previews).
 * @param {number|null|undefined} poolEntrantCount — `pool_entrant_count` / `squares_pool_entrant_count`
 * @returns {number|null}
 */
export function computeSquaresTotalPrizePoolCoins(poolEntrantCount) {
    const nk = Math.floor(Number(poolEntrantCount));
    if (!Number.isFinite(nk) || nk <= 0) return null;
    const rake = rakeForEntrantCount(nk);
    return Math.round(nk * SQUARES_ENTRANCE_FEE * (1 - rake));
}

/**
 * First-place (top) prize in coins: post-rake pool × 1st-place share. Same as backend `computeTopPrizeFirstPlaceFromN`.
 * @param {number|null|undefined} poolEntrantCount
 * @returns {number|null}
 */
export function computeSquaresTopPrizeFirstPlaceCoins(poolEntrantCount) {
    const nk = Math.floor(Number(poolEntrantCount));
    if (!Number.isFinite(nk) || nk <= 0) return null;
    const rake = rakeForEntrantCount(nk);
    const shares = prizeSharesForEntrantCount(nk);
    const totalPrizePool = nk * SQUARES_ENTRANCE_FEE * (1 - rake);
    const share = shares[0] != null ? Number(shares[0]) : 0.5;
    return Math.round(totalPrizePool * share);
}

/** Chakra Petch 700 12px / 100% — right-aligned lobby match card secondary text (status, scores, ov label). */
export const SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS =
    "normal-case font-chakra text-[12px] !font-bold not-italic leading-[100%] tracking-[0] text-right align-middle [leading-trim:none]";

/** Chakra Petch 400 10px / 100% — match play sticky status (STARTS IN …, SCHEDULED, etc.). */
export const SQUARES_MATCH_PLAY_STATUS_TEXT_CLASS =
  "align-middle font-chakra text-[10px] font-normal not-italic leading-[100%] tracking-[0%] [leading-trim:none]";

/** Anton 12px / 12px — over header live status (IN PLAY, OVER FINISHED, etc.). */
export const SQUARES_OVER_HEADER_STATUS_TEXT_CLASS =
    "font-anton font-normal not-italic text-[12px] leading-[12px] uppercase tracking-[0%] [leading-trim:none]";

/** GAME OPEN / GAME NOT OPEN YET in over odds header (12px mobile; 20px desktop). */
export const SQUARES_OVER_HEADER_GAME_STATUS_TEXT_CLASS =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-left text-[#D2FF00] [leading-trim:none] lg:text-[20px] lg:leading-[20px]";

/** Horizontal inset for lime status copy beside the OVER pill. */
export const SQUARES_OVER_HEADER_GAME_STATUS_INSET_CLASS = "pl-1.5 sm:pl-2";

/** Over header meta copy (players, prize, innings, entrance): 12px mobile, 20px desktop. */
export const SQUARES_OVER_HEADER_META_TEXT_CLASS =
  "font-anton font-normal not-italic uppercase leading-[12px] tracking-[0%] [leading-trim:none] text-[12px] lg:text-[20px] lg:leading-[12px]";

/** Right-aligned over header meta (players · prize · entrance). */
export const SQUARES_OVER_HEADER_META_TEXT_RIGHT_CLASS =
  `${SQUARES_OVER_HEADER_META_TEXT_CLASS} text-right text-white`;

/** ACTIVE / FINISHED / innings segment tabs (white pill when selected). */
export const SQUARES_SECTION_TAB_GROUP_CLASS = "flex min-h-0 min-w-0 items-center gap-0";

export const SQUARES_SECTION_TAB_BUTTON_BASE_CLASS =
  "box-border flex h-[30px] shrink-0 items-center justify-center rounded-none border-0 font-anton text-[12px] font-normal not-italic uppercase leading-[20px] tracking-[0%] [leading-trim:none] transition-colors";

/** @param {boolean} isSelected */
export function squaresSectionTabButtonClass(isSelected) {
  return isSelected
    ? `${SQUARES_SECTION_TAB_BUTTON_BASE_CLASS} bg-white text-[#1B062F]`
    : `${SQUARES_SECTION_TAB_BUTTON_BASE_CLASS} bg-white/15 text-white hover:bg-white/25`;
}

/** White “BALL N” badge beside in-play commentary (ball-results strip). */
export const SQUARES_INPLAY_BALL_NUMBER_BADGE_CLASS =
    "box-border flex h-[18px] w-[35px] shrink-0 flex-row items-center justify-center rounded-none bg-white font-anton text-[10px] font-normal not-italic uppercase leading-[10px] tracking-[0%] text-[#1B062F] [leading-trim:none] md:h-[33px] md:w-[60px] md:min-h-[33px] md:max-h-[33px] md:min-w-[60px] md:max-w-[60px]";

/** Settled / completed over ball result square (not the in-play pink cell). */
export const SQUARES_OVER_BALL_RESULT_CELL_CLASS =
    "box-border border-2 border-solid border-transparent bg-[#642CAD] text-white";

/** Live ball-results strip: current (in-play) cell — pink fill, white border, white dots/text. */
export const SQUARES_CURRENT_BALL_RESULT_CELL_CLASS =
    "box-border border-2 border-solid border-white bg-[#F73261] text-white";
export {
    BALL_IN_PROGRESS_DOT_BASE_CLASS as SQUARES_CURRENT_BALL_RESULT_DOT_BASE_CLASS,
    BALL_IN_PROGRESS_DOT_OPACITY_CLASSES as SQUARES_CURRENT_BALL_RESULT_DOT_OPACITY_CLASSES,
    BALL_IN_PROGRESS_DOT_DELAYS_MS as SQUARES_CURRENT_BALL_RESULT_DOT_DELAYS_MS,
} from "../../Cricket/ballInProgressDotStyles";

/** In-play over leaderboard: pick cell waiting for the current ball result — lime border, purple fill. */
export const SQUARES_LEADERBOARD_INPLAY_WAITING_CELL_CLASS =
    "box-border border-2 border-[#D2FF00] bg-[#6333A3] text-white/70";

/** Leaderboard rank number (Anton 12px mobile; 20px desktop). */
export const SQUARES_LEADERBOARD_RANK_TEXT_CLASS =
    "flex min-w-[12px] shrink-0 items-center justify-center align-middle text-center font-anton text-[12px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white tabular-nums [leading-trim:none] lg:text-[20px] lg:leading-[12px]";

/** Rank column width in live over player rows (coins sit slightly left of this column). */
export const SQUARES_LEADERBOARD_RANK_COL_WIDTH_CLASS = "w-[26px] min-w-[26px] max-w-[26px]";

/** Leaderboard player name (Anton 10px mobile; 16px desktop). */
export const SQUARES_LEADERBOARD_PLAYER_NAME_TEXT_CLASS =
    "min-w-0 truncate align-middle text-left font-anton text-[10px] font-normal not-italic uppercase leading-[22px] tracking-[0%] text-white [leading-trim:none] lg:text-[16px] lg:leading-[22px]";

/** In-play row avatar (24px mobile; ~44px desktop). */
export const SQUARES_LEADERBOARD_INPLAY_AVATAR_SIZE_CLASS =
    "h-6 w-6 shrink-0 lg:h-[44px] lg:w-[44px]";

/** In-play pick cell size (23×22 mobile; 42×40 desktop). */
export const SQUARES_LEADERBOARD_INPLAY_PICK_CELL_SIZE_CLASS =
    "h-[22px] w-[23px] lg:h-[40px] lg:w-[42px]";

/** Pull name column slightly toward avatar (in-play rows); extra gap on desktop. */
export const SQUARES_LEADERBOARD_PLAYER_NAME_OFFSET_CLASS =
    "-ml-1 sm:-ml-1.5 lg:ml-0 lg:pl-2";

/** Extra gap between avatar and name when the over header shows OVER FINISHED. */
export const SQUARES_LEADERBOARD_PLAYER_NAME_FINISHED_OFFSET_CLASS = "pl-1 sm:pl-1.5 lg:pl-3";

/** Live over leaderboard row grid: rank | avatar | name | balls (fixed) | chips (fixed). */
export const SQUARES_LEADERBOARD_INPLAY_ROW_GRID_COLS =
    "grid-cols-[26px_24px_minmax(0,1fr)_143px_86px] lg:grid-cols-[26px_44px_minmax(0,1fr)_257px_150px]";

/** Winners / modal leaderboard row grid: identity | name | balls (fixed) | chips (fixed). */
export const SQUARES_LEADERBOARD_WINNERS_ROW_GRID_COLS =
    "grid-cols-[auto_minmax(0,1fr)_143px_96px]";

/** Six pick cells: 6×23px + gaps mobile; 6×42px + gaps desktop. */
export const SQUARES_LEADERBOARD_BALLS_COL_CLASS =
    "col-start-4 box-border flex w-[143px] min-w-[143px] max-w-[143px] shrink-0 justify-end gap-px lg:w-[257px] lg:min-w-[257px] lg:max-w-[257px]";

/** Same fixed ball strip in winners modal rows (column 3). */
export const SQUARES_LEADERBOARD_BALLS_COL_WINNERS_CLASS =
    "col-start-3 box-border flex w-[143px] min-w-[143px] max-w-[143px] shrink-0 justify-end gap-px";

/** Reserved chips column (in-play: amount + chevron). */
export const SQUARES_LEADERBOARD_CHIPS_COL_INPLAY_CLASS =
    "col-start-5 box-border flex w-[86px] min-w-[86px] max-w-[86px] shrink-0 items-center justify-end gap-1.5 lg:w-[150px] lg:min-w-[150px] lg:max-w-[150px] lg:gap-2.5";

/** Leaderboard running score (Anton 12px mobile; 20px desktop, line-height 100%, right-aligned). */
export const SQUARES_LEADERBOARD_SCORE_TEXT_CLASS =
    "mr-0.5 whitespace-nowrap text-right align-middle font-anton text-[12px] font-normal not-italic leading-[100%] tracking-[0%] text-white tabular-nums [leading-trim:none] lg:mr-2 lg:text-[20px]";

/** Leaderboard chip icon beside score (20px mobile; 36.5px desktop). */
export const SQUARES_LEADERBOARD_CHIP_ICON_SIZE_CLASS =
    "h-5 w-5 shrink-0 object-contain lg:h-[36.5px] lg:w-[36.5px]";

/** Expand/collapse chevron in leaderboard rows (18px mobile; 29px desktop). */
export const SQUARES_LEADERBOARD_ROW_CHEVRON_SIZE_CLASS =
    "h-[18px] w-[18px] shrink-0 object-contain lg:h-[29px] lg:w-[29px]";

/** Chevron toggle button — extra inset from the row edge on desktop. */
export const SQUARES_LEADERBOARD_ROW_CHEVRON_BUTTON_CLASS =
    "group flex shrink-0 rounded-none border-0 bg-transparent p-0 outline-none ring-0 transition-colors focus-visible:ring-2 focus-visible:ring-[#D2FF00]/80 lg:mr-2";

/** Reserved chips column (winners: two amount rows + chevron). */
export const SQUARES_LEADERBOARD_CHIPS_COL_WINNERS_CLASS =
    "col-start-4 box-border flex w-[96px] min-w-[96px] max-w-[96px] shrink-0 flex-row items-center justify-end gap-1 sm:gap-1.5";

/** “YOU'RE IN THE GAME!” banner above submitted picks (match-play grid overlay). */
export const SQUARES_IN_GAME_BANNER_CLASS =
    "box-border flex h-[40px] min-h-[40px] max-h-[40px] w-full shrink-0 items-center justify-center bg-lime font-anton text-[12px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-center text-main [leading-trim:none]";

/** Compact in-game banner for lobby embeds (e.g. league Play card). */
export const SQUARES_IN_GAME_BANNER_COMPACT_CLASS =
    "box-border flex h-[28px] min-h-[28px] max-h-[28px] w-full shrink-0 items-center justify-center bg-lime font-anton text-[11px] font-normal not-italic uppercase leading-[11px] tracking-[0%] text-center text-main [leading-trim:none]";

/** Panel behind the in-game banner + picks summary — darker shell so #9747FF ball headers read as boxes. */
export const SQUARES_IN_GAME_PANEL_CLASS =
    "flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto bg-[linear-gradient(to_right,#4e2384,#4d287a)] px-3 py-3 text-center sm:gap-5 sm:px-4 sm:py-4";

/** Over odds bar (OVER N row) and odds grid thead row height. */
export const SQUARES_OVER_ODDS_BAR_HEIGHT_CLASS = "h-[55px] min-h-[55px] max-h-[55px]";

/** In-play current-over header row — same 55px height as the odds bar at all breakpoints. */
export const SQUARES_INPLAY_OVER_HEADER_HEIGHT_CLASS = SQUARES_OVER_ODDS_BAR_HEIGHT_CLASS;

/** In-play ball strip — phone keeps compact strip; tablet+ uses 73px design height. */
export const SQUARES_INPLAY_BALL_STRIP_HEIGHT_CLASS =
  "h-[40px] min-h-[40px] md:h-[73px] md:min-h-[73px] md:max-h-[73px]";

/** In-play current-over leaderboard player row height (desktop only). */
export const SQUARES_LEADERBOARD_INPLAY_ROW_HEIGHT_CLASS =
  "lg:h-[73px] lg:min-h-[73px] lg:max-h-[73px]";

/** In-play leaderboard row layout — mobile inset; full bleed from tablet up. */
export const SQUARES_LEADERBOARD_INPLAY_ROW_LAYOUT_CLASS =
  "box-border w-full overflow-visible py-1.5 pl-1 pr-1.5 sm:py-2 sm:pl-1.5 sm:pr-2 md:py-0 md:pl-0 md:pr-0";

/** In-play leaderboard list — mobile lime frame; full bleed from tablet up. */
export const SQUARES_LEADERBOARD_INPLAY_LIST_CLASS =
  "box-border flex w-full flex-col gap-px overflow-x-visible overflow-y-visible border-2 border-[#D2FF00] bg-[#652dae] md:border-0";

/** In-play leaderboard outer wrapper — centered on mobile; full width from tablet up. */
export const SQUARES_LEADERBOARD_INPLAY_OUTER_CLASS =
  "mx-auto w-full max-w-[520px] overflow-visible px-1 pt-3 sm:max-w-[560px] sm:pt-4 md:max-w-none md:px-0 md:pt-0";

/** Lime OVER N pill in the over odds header row. */
export const SQUARES_OVER_ODDS_PILL_CLASS =
  "box-border flex h-[55px] w-[50px] min-w-[50px] max-w-[50px] shrink-0 items-center justify-center rounded-none bg-[#D2FF00] p-0 font-anton text-[11px] font-normal uppercase leading-none tracking-[0%] text-[#1B062F] [leading-trim:none] sm:text-[12px]";

/** Lime OVER N pill in the in-play current-over header. */
export const SQUARES_INPLAY_OVER_PILL_CLASS = SQUARES_OVER_ODDS_PILL_CLASS;

/** Fixed height for an expanded over card when the user has already entered (55px header + 206px body). */
export const SQUARES_IN_GAME_OVER_CARD_HEIGHT_CLASS = "h-[261px] min-h-[261px] max-h-[261px]";
export const SQUARES_IN_GAME_OVER_BODY_HEIGHT_CLASS = "h-[206px] min-h-[206px] max-h-[206px]";

/** Accordion collapse for over body (grid 1fr ↔ 0fr). Duration matches `SQUARES_OVER_SECTION_COLLAPSE_MS`. */
export const SQUARES_OVER_SECTION_COLLAPSE_GRID_CLASS =
  "grid overflow-hidden transition-[grid-template-rows] duration-[560ms] ease-[cubic-bezier(0.22,1,0.36,1)]";
/** Fade collapsing over body content with the height animation. */
export const SQUARES_OVER_SECTION_COLLAPSE_INNER_CLASS =
  "min-h-0 transition-opacity duration-[560ms] ease-[cubic-bezier(0.22,1,0.36,1)]";

/** Gap between next-over odds grid cells (`border-spacing`). */
export const SQUARES_ODDS_GRID_CELL_GAP_PX = 2;

/** Next-over odds grid table width: 7×50px + gaps mobile; 7×87px + gaps desktop. */
export const SQUARES_ODDS_GRID_TABLE_WIDTH_CLASS = "w-[362px] lg:w-[621px]";
export const SQUARES_ODDS_GRID_TABLE_MIN_WIDTH_CLASS = "min-w-[362px] lg:min-w-[621px]";
export const SQUARES_ODDS_GRID_MAX_WIDTH_CLASS = "max-w-[362px] lg:max-w-[621px]";

const SQUARES_ODDS_GRID_CELL_LG_HEIGHT_CLASS =
    "lg:h-[87px] lg:min-h-[87px] lg:max-h-[87px]";

const SQUARES_ODDS_GRID_CELL_LG_WIDTH_CLASS =
    "lg:w-[87px] lg:min-w-[87px] lg:max-w-[87px]";

/** Full-bleed desktop column: equal column width from table `w-full`. */
export const SQUARES_ODDS_GRID_CELL_LG_FLUID_WIDTH_CLASS =
    "lg:w-auto lg:min-w-0 lg:max-w-none";

const SQUARES_ODDS_GRID_HEADER_CELL_LG_HEIGHT_CLASS =
    "lg:h-[55.59px] lg:min-h-[55.59px] lg:max-h-[55.59px]";

/** Next-over odds grid body cell shell (50×47 mobile; 87×87 desktop). */
export const SQUARES_ODDS_GRID_BODY_CELL_SIZE_CLASS =
    `box-border h-[47px] min-h-[47px] max-h-[47px] w-[50px] min-w-[50px] max-w-[50px] border-0 p-0 align-middle ${SQUARES_ODDS_GRID_CELL_LG_HEIGHT_CLASS} ${SQUARES_ODDS_GRID_CELL_LG_WIDTH_CLASS}`;

/** Body cell for full-bleed desktop grid — height fixed, width stretches. */
export const SQUARES_ODDS_GRID_BODY_CELL_FLUID_SIZE_CLASS =
    `box-border h-[47px] min-h-[47px] max-h-[47px] w-[50px] min-w-[50px] max-w-[50px] border-0 p-0 align-middle ${SQUARES_ODDS_GRID_CELL_LG_HEIGHT_CLASS} ${SQUARES_ODDS_GRID_CELL_LG_FLUID_WIDTH_CLASS}`;

/** Next-over odds grid header cell shell (50×55 mobile; 87×55.59 desktop). */
export const SQUARES_ODDS_GRID_HEADER_CELL_SIZE_CLASS =
    `box-border h-[55px] min-h-[55px] max-h-[55px] w-[50px] min-w-[50px] max-w-[50px] border-0 p-0 align-middle ${SQUARES_ODDS_GRID_HEADER_CELL_LG_HEIGHT_CLASS} ${SQUARES_ODDS_GRID_CELL_LG_WIDTH_CLASS}`;

/** Header cell for full-bleed desktop grid — height fixed, width stretches. */
export const SQUARES_ODDS_GRID_HEADER_CELL_FLUID_SIZE_CLASS =
    `box-border h-[55px] min-h-[55px] max-h-[55px] w-[50px] min-w-[50px] max-w-[50px] border-0 p-0 align-middle ${SQUARES_ODDS_GRID_HEADER_CELL_LG_HEIGHT_CLASS} ${SQUARES_ODDS_GRID_CELL_LG_FLUID_WIDTH_CLASS}`;

/** Full-bleed odds table (desktop side column): stretch to column width. */
export const SQUARES_ODDS_GRID_TABLE_FLUID_DESKTOP_CLASS =
    "w-full min-w-[362px] max-w-none lg:min-w-0";

/**
 * Compact embed (league lobby card): short rows, full-width columns, no lg height bump.
 * Keeps the interactive grid readable without the match-play 47/87px cell heights.
 */
export const SQUARES_ODDS_GRID_BODY_CELL_COMPACT_SIZE_CLASS =
    "box-border h-[28px] min-h-[28px] max-h-[28px] w-auto min-w-0 border-0 p-0 align-middle";
export const SQUARES_ODDS_GRID_HEADER_CELL_COMPACT_SIZE_CLASS =
    "box-border h-[32px] min-h-[32px] max-h-[32px] w-auto min-w-0 border-0 p-0 align-middle";
export const SQUARES_ODDS_GRID_TABLE_COMPACT_WIDTH_CLASS = "w-full min-w-0 max-w-none";
export const SQUARES_ODDS_GRID_COMPACT_MAX_WIDTH_CLASS = "w-full max-w-none";
export const SQUARES_ODDS_GRID_BODY_CELL_COMPACT_INNER_CLASS =
    "flex h-[28px] w-full flex-row items-center justify-center";
export const SQUARES_ODDS_GRID_HEADER_CELL_COMPACT_INNER_CLASS =
    "flex h-[32px] w-full flex-row items-center justify-center";
export const SQUARES_ODDS_GRID_ODDS_CELL_COMPACT_INNER_CLASS =
    "relative box-border flex h-[28px] w-full flex-col items-center justify-center";
export const SQUARES_ODDS_GRID_COMPACT_CHIP_POOL_TEXT_CLASS =
    "font-anton text-[10px] font-normal not-italic uppercase leading-none tracking-[0%] text-white tabular-nums [leading-trim:none]";
export const SQUARES_ODDS_GRID_COMPACT_CHIP_POOL_ICON_SIZE_CLASS =
    "h-3.5 w-3.5 shrink-0 object-contain";
export const SQUARES_ODDS_GRID_COMPACT_BALL_HEADER_TEXT_CLASS =
    "text-center font-anton text-[9px] font-normal not-italic uppercase leading-none tracking-[0%] text-white [leading-trim:none]";
export const SQUARES_ODDS_GRID_COMPACT_RUN_LABEL_TEXT_CLASS =
    "text-center font-anton text-[8px] font-normal not-italic uppercase leading-none tracking-[0%] text-white [leading-trim:none]";
export const SQUARES_ODDS_GRID_COMPACT_SELECTED_CHIP_TEXT_CLASS =
    "font-anton text-[9px] font-normal not-italic leading-none tracking-[0%] text-center text-[#1B062F] [leading-trim:none]";
export const SQUARES_ODDS_GRID_COMPACT_SELECTED_CHIP_ICON_SIZE_CLASS =
    "h-2.5 w-2.5 shrink-0 object-contain";
export const SQUARES_ODDS_GRID_COMPACT_ODDS_TEXT_CLASS =
    "font-chakra text-[9px] font-normal not-italic leading-none tracking-[0%] text-center [leading-trim:none]";

/** Inner flex fill for run label / ball header cells. */
export const SQUARES_ODDS_GRID_BODY_CELL_INNER_CLASS =
    "flex h-[47px] w-full flex-row items-center justify-center lg:h-[87px]";

/** Inner flex fill for header row cells (chip pool, BALL N). */
export const SQUARES_ODDS_GRID_HEADER_CELL_INNER_CLASS =
    "flex h-[55px] w-full flex-row items-center justify-center lg:h-[55.59px] lg:min-h-[55.59px] lg:max-h-[55.59px]";

/** Inner flex fill for interactive odds cells. */
export const SQUARES_ODDS_GRID_ODDS_CELL_INNER_CLASS =
    "relative box-border flex h-[47px] w-full flex-col items-center justify-center lg:h-[87px]";

/** Top-left chip pool count (Anton 10px mobile; 20px desktop). */
export const SQUARES_ODDS_GRID_CHIP_POOL_TEXT_CLASS =
    "font-anton text-[10px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white tabular-nums [leading-trim:none] lg:text-[20px] lg:leading-[12px]";

/**
 * League Squares chip-pool count — a touch smaller than public so large bankrolls fit,
 * but still readable in the lobby corner cell.
 * Compact lobby: 10px; match-play: 10px / 14px desktop.
 */
export const SQUARES_ODDS_GRID_LEAGUE_CHIP_POOL_TEXT_CLASS =
    "font-anton text-[10px] font-normal not-italic uppercase leading-[11px] tracking-[0%] text-white tabular-nums [leading-trim:none] lg:text-[14px] lg:leading-[14px]";
export const SQUARES_ODDS_GRID_COMPACT_LEAGUE_CHIP_POOL_TEXT_CLASS =
    "font-anton text-[10px] font-normal not-italic uppercase leading-none tracking-[0%] text-white tabular-nums [leading-trim:none]";

/** Top-left chip pool icon (16px mobile; 33px desktop). */
export const SQUARES_ODDS_GRID_CHIP_POOL_ICON_SIZE_CLASS =
    "h-[15.8px] w-[15.8px] shrink-0 object-contain lg:h-[33px] lg:w-[33px]";

/** BALL N column headers (Anton 11px mobile; 20px / 15px lh desktop). */
export const SQUARES_ODDS_GRID_BALL_HEADER_TEXT_CLASS =
    "text-center font-anton text-[11px] font-normal not-italic uppercase leading-none tracking-[0%] text-white [leading-trim:none] lg:text-[20px] lg:leading-[15px]";

/** Run label column (0 RUNS …) — same as ball headers. */
export const SQUARES_ODDS_GRID_RUN_LABEL_TEXT_CLASS =
    "text-center font-anton text-[11px] font-normal not-italic uppercase leading-none tracking-[0%] text-white [leading-trim:none] lg:text-[20px] lg:leading-[15px]";

/** Selected cell chip count on white tile (Anton 12px mobile; 20px desktop). */
export const SQUARES_ODDS_GRID_SELECTED_CHIP_TEXT_CLASS =
    "font-anton text-[12px] font-normal not-italic leading-[100%] tracking-[0%] text-center align-middle text-[#1B062F] [leading-trim:none] lg:text-[20px]";

/** Selected cell chip icon (19px mobile; 19px desktop). */
export const SQUARES_ODDS_GRID_SELECTED_CHIP_ICON_SIZE_CLASS =
    "h-[19px] w-[19px] shrink-0 object-contain lg:h-[19px] lg:w-[19px]";

/** Unselected odds value (Chakra 11px mobile; 14px desktop). */
export const SQUARES_ODDS_GRID_ODDS_TEXT_CLASS =
    "font-chakra text-[11px] font-normal not-italic leading-[100%] tracking-[0%] text-center align-middle [leading-trim:none] lg:text-[14px]";

/** Match play page shell background (left → right). */
export const SQUARES_MATCH_PLAY_PAGE_BG_CLASS = "bg-gradient-to-r from-[#1B062F] to-[#381660]";
export const SQUARES_MATCH_PLAY_PAGE_BG_STYLE = {
    background: "linear-gradient(to right, #1B062F 0%, #381660 100%)",
};

/**
 * Lobby kill switch: set env `SQUARES_LOBBY_ENABLED=false` (or `0` / `off`) to hide matches and show empty state.
 * Omit or any other value = lobby on (default).
 */
export function isSquaresLobbyEnabled() {
  const v = process.env.SQUARES_LOBBY_ENABLED;
  if (v == null || v === "") return true;
  const s = String(v).trim().toLowerCase();
  return s !== "false" && s !== "0" && s !== "off";
}

/** Decimal `match.format` for Test cricket — excluded from Squares. */
export const SQUARES_TEST_FORMAT_CODE = 999;

/** Decimal `match.format` for The Hundred (100 balls) — excluded from Squares. */
export const SQUARES_HUNDRED_FORMAT_CODE = 100;
